import type { Observation } from '@/types';

export type TeamSide = 'home' | 'away';

export interface TeamSideContext {
  homeClubCle?: string;
  awayClubCle?: string;
  homeClubId?: string;
  awayClubId?: string;
  homeTeamName?: string;
  awayTeamName?: string;
}

function norm(value: string | undefined): string {
  return (value || '').trim().toLowerCase();
}

/** Parse la valeur brute du champ API `equipe` / `team`. */
export function parseEquipeRaw(raw: string | undefined): TeamSide | undefined {
  if (!raw?.trim()) return undefined;
  const n = raw.trim().toLowerCase();
  if (n === 'home' || n === 'domicile' || n === 'local' || n === 'h' || n === '1') return 'home';
  if (
    n === 'away' ||
    n === 'exterieur' ||
    n === 'extérieur' ||
    n === 'visitor' ||
    n === 'visiteur' ||
    n === 'a' ||
    n === '2'
  ) {
    return 'away';
  }
  return undefined;
}

function matchesRef(obsRef: string, candidate: string | undefined): boolean {
  if (!candidate?.trim()) return false;
  return norm(obsRef) === norm(candidate);
}

/** Infère domicile / extérieur à partir d'une clé ou id club. */
export function resolveTeamFromClubRef(
  clubRef: string | undefined,
  ctx: TeamSideContext,
): TeamSide | undefined {
  if (!clubRef?.trim()) return undefined;
  if (
    matchesRef(clubRef, ctx.homeClubCle) ||
    matchesRef(clubRef, ctx.homeClubId)
  ) {
    return 'home';
  }
  if (
    matchesRef(clubRef, ctx.awayClubCle) ||
    matchesRef(clubRef, ctx.awayClubId)
  ) {
    return 'away';
  }
  return undefined;
}

function isSideLabel(value: string | undefined): boolean {
  return parseEquipeRaw(value) !== undefined;
}

/** Infère domicile / extérieur à partir du nom d'équipe. */
export function resolveTeamFromTeamNames(
  teamLabel: string | undefined,
  homeTeam?: string,
  awayTeam?: string,
): TeamSide | undefined {
  const label = norm(teamLabel);
  if (!label) return undefined;
  if (isSideLabel(teamLabel)) return parseEquipeRaw(teamLabel);
  const home = isSideLabel(homeTeam) ? '' : norm(homeTeam);
  const away = isSideLabel(awayTeam) ? '' : norm(awayTeam);
  if (home && (label === home || label.includes(home) || home.includes(label))) return 'home';
  if (away && (label === away || label.includes(away) || away.includes(label))) return 'away';
  return undefined;
}

function refsMatch(a: string, b: string): boolean {
  const x = a.trim().toLowerCase();
  const y = b.trim().toLowerCase();
  if (!x || !y) return false;
  if (x === y) return true;
  // Clé vs id numérique, ou sous-chaîne stable (UUID, etc.)
  if (x.length >= 6 && y.length >= 6 && (x.includes(y) || y.includes(x))) return true;
  return false;
}

export interface ObservationEditProfile {
  pseudo?: string;
  email?: string;
  prenom?: string;
  nom?: string;
}

/** Vérifie si l'observation appartient au Tifosi connecté (clé ou id). */
export function matchesObservationOwner(
  observation: { tifosi?: string },
  myTifosiCle: string | undefined,
): boolean {
  const me = (myTifosiCle || '').trim();
  if (!me) return false;
  const owner = (observation.tifosi || '').trim();
  if (!owner) return false;
  return refsMatch(owner, me);
}

/** Compare l'auteur affiché (pseudo, email, nom) au profil connecté. */
export function matchesObservationAuthor(
  observation: { author?: string },
  profile: ObservationEditProfile,
): boolean {
  const author = (observation.author || '').trim().toLowerCase();
  if (!author) return false;
  const candidates = [
    profile.pseudo,
    profile.email,
    [profile.prenom, profile.nom].filter(Boolean).join(' '),
    profile.prenom,
    profile.nom,
  ]
    .filter((v): v is string => Boolean(v?.trim()))
    .map((v) => v.trim().toLowerCase());
  return candidates.some((c) => c === author || author.includes(c) || c.includes(author));
}

/**
 * Autorise l'édition : liste « Mes observations », clé tifosi, ou auteur reconnu.
 */
export function canEditObservation(
  observation: { tifosi?: string; author?: string },
  myTifosiCle: string | undefined,
  options?: {
    trustedMineList?: boolean;
    isAuthenticated?: boolean;
    profile?: ObservationEditProfile;
  },
): boolean {
  if (options?.trustedMineList && options?.isAuthenticated) return true;
  if (matchesObservationOwner(observation, myTifosiCle)) return true;
  if (options?.profile && matchesObservationAuthor(observation, options.profile)) return true;
  return false;
}

/** Détermine l'équipe observée pour préremplir le formulaire ou l'affichage. */
export function resolveObservationTeamSide(
  obs: Pick<Observation, 'team' | 'club' | 'teamName' | 'homeTeam' | 'awayTeam' | 'homeClubId' | 'awayClubId'>,
  ctx: TeamSideContext = {},
): TeamSide {
  if (obs.team === 'home' || obs.team === 'away') return obs.team;

  const homeTeam = ctx.homeTeamName ?? obs.homeTeam;
  const awayTeam = ctx.awayTeamName ?? obs.awayTeam;

  const fromObsClubIds = resolveTeamFromClubRef(obs.club, {
    homeClubId: obs.homeClubId,
    awayClubId: obs.awayClubId,
  });
  if (fromObsClubIds) return fromObsClubIds;

  const fromCtxClub = resolveTeamFromClubRef(obs.club, ctx);
  if (fromCtxClub) return fromCtxClub;

  const fromTeamName = resolveTeamFromTeamNames(obs.teamName ?? obs.club, homeTeam, awayTeam);
  if (fromTeamName) return fromTeamName;

  return 'home';
}
